import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Observation } from 'src/app/domain/observations/types/observations.types';
import {
  getMeasurementValue,
  getPresentMeasurementTypes,
  type MeasurementTypeId,
} from 'src/app/domain/observations/types/observations.lookups';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Canonical card for a single {@link Observation} record. Displays the type, formatted
 * value, date, skill/session context, and optional notes.
 *
 * Since a Observation document now carries values for multiple types at once,
 * callers can either pass an explicit `type` input to show a specific type's
 * value, or let the card pick the first populated type on the document.
 *
 * @security FERPA — measurements are tied to a student and may include session notes
 * with observation context. Do not log the studentId, notes, or source.
 *
 * @feature shared
 */
@Component({
  selector: 'app-observation-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CardComponent, ChipComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <app-card [button]="clickable()" [ariaLabel]="'Open measurement ' + (effectiveType() ?? '')" (click)="onClick()">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          @if (effectiveType(); as type) {
            <app-chip color="purple" size="xs">{{ type }}</app-chip>
          }
          <p class="mt-2 text-3xl font-bold text-body">{{ formattedValue() }}</p>
          @if (skillName()) {
            <p class="mt-1 truncate text-sm text-muted">{{ skillName() }}</p>
          }
        </div>
      </div>

      <div class="mt-4 grid grid-cols-2 gap-3 border-t border-outline-low pt-3 text-xs">
        <div class="flex items-center gap-2">
          <app-icon name="calendar" size="xxs" color="medium" />
          <span class="truncate text-muted">{{ dateLabel() }}</span>
        </div>
        @if (measurement().setting) {
          <div class="flex items-center gap-2">
            <app-icon name="location" size="xxs" color="medium" />
            <span class="truncate text-muted">{{ measurement().setting }}</span>
          </div>
        }
        @if (measurement().source) {
          <div class="flex items-center gap-2">
            <app-icon name="person" size="xxs" color="medium" />
            <span class="truncate text-muted">{{ measurement().source }}</span>
          </div>
        }
        @if (measurement().trialDuration) {
          <div class="flex items-center gap-2">
            <app-icon name="timer" size="xxs" color="medium" />
            <span class="truncate text-muted">{{ durationLabel() }}</span>
          </div>
        }
      </div>

      @if (measurement().additionalNotes) {
        <p class="mt-3 line-clamp-2 border-t border-outline-low pt-3 text-sm text-muted">
          {{ measurement().additionalNotes }}
        </p>
      }
    </app-card>
  `,
})
export class ObservationCardComponent {
  /** The measurement to display. @security FERPA. */
  readonly measurement = input.required<Observation>();

  /** Which measurement type to display. Defaults to the first populated type. */
  readonly type = input<MeasurementTypeId | null>(null);

  /** Optional resolved skill name (Observation stores skillId). */
  readonly skillName = input<string | undefined>(undefined);

  /** Whether the card is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the card is clicked. */
  readonly cardClick = output<Observation>();

  /** Type currently driving the displayed value. */
  readonly effectiveType = computed<MeasurementTypeId | null>(() => {
    const explicit = this.type();
    if (explicit) return explicit;
    return getPresentMeasurementTypes(this.measurement())[0] ?? null;
  });

  /** Formatted value for the currently displayed type. */
  readonly formattedValue = computed(() => {
    const m = this.measurement();
    const type = this.effectiveType();
    if (!type) return '—';
    const v = getMeasurementValue(m, type);
    switch (type) {
      case 'ACCURACY':
      case 'ENGAGEMENT':
      case 'TIME_ON_TASK':
      case 'OCCURRENCE':
        return v != null ? `${Math.round(v * 100)}%` : '—';
      case 'FLUENCY':
        return v != null ? `${v} correct/min` : '—';
      case 'FREQUENCY':
        return v != null ? `${v}` : '—';
      case 'DURATION':
        return v != null ? formatSeconds(v) : '—';
      case 'LATENCY':
        return v != null ? `${v}s` : '—';
      case 'RATE':
        return v != null ? `${v}/min` : '—';
      case 'MAGNITUDE':
        return v != null ? `${v}` : '—';
      default:
        return v != null ? `${v}` : '—';
    }
  });

  readonly dateLabel = computed(() => {
    const d = this.measurement().inputtedDate;
    if (!d) return 'No date';
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  });

  readonly durationLabel = computed(() => {
    const s = this.measurement().trialDuration;
    if (!s) return '';
    return formatSeconds(s);
  });

  onClick(): void {
    if (this.clickable()) {
      this.cardClick.emit(this.measurement());
    }
  }
}

/** Format seconds as `Xm Ys` or `Xs`. */
function formatSeconds(totalSeconds: number): string {
  const s = Math.round(totalSeconds);
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
}
